'use client';

import { ColumnDef } from '@tanstack/react-table';
import { ExternalLink, Repeat } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from "@/lib/money";
import { SubscriptionResourceBadge } from '@/app/subscriptions/components/SubscriptionResourceBadge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

// Generic subscription type for embedded table
type SubscriptionRow = {
  id: string;
  status: string;
  amount?: number;
  currency?: string;
  nextBillingDate?: Date;
  priceInterval?: string;
  priceIntervalCount?: number;
};

// Get badge variant based on subscription status
function getSubscriptionStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'default';
    case 'trialing':
      return 'secondary';
    case 'past_due':
    case 'canceled':
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

function formatInterval(interval: string, intervalCount: number, t: (key: string) => string) {
  const key = `filters.plans.${interval}`;
  const translated = t(key);
  const intervalName = translated === key ? interval.charAt(0).toUpperCase() + interval.slice(1) : translated;
  if (intervalCount === 1) return intervalName;
  return `${intervalCount} ${intervalName}`;
}

/**
 * Lightweight subscription table columns for embedded use in detail pages
 * Focuses on essential information only
 */
export function useEmbeddedSubscriptionsTableColumns(): ColumnDef<SubscriptionRow>[] {
  const { t } = useTranslation('subscriptions');

  const ActionsCell = ({ subscriptionId }: { subscriptionId: string }) => {
    const { orgRoutes } = useOrgNavigate();
    return (
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" asChild>
          <Link to={orgRoutes.SUBSCRIPTION_DETAILS(subscriptionId)}>
            <ExternalLink className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    );
  };

  return React.useMemo<ColumnDef<SubscriptionRow>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('table.columns.subscriptionId'),
        cell: ({ row }) => {
          const subscription = row.original;
          return <SubscriptionResourceBadge subscriptionId={subscription.id} />;
        },
        size: 180,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('table.columns.status'),
        cell: ({ row }) => {
          const status = row.original.status;
          const key = `filters.statuses.${status?.toLowerCase()}`;
          const translated = t(key);
          const label = translated === key ? status : translated;
          return <Badge variant={getSubscriptionStatusVariant(status)}>{label}</Badge>;
        },
        size: 120,
      },
      {
        id: 'plan',
        accessorKey: 'priceInterval',
        header: t('table.columns.plan'),
        cell: ({ row }) => {
          const subscription = row.original;
          const interval = subscription.priceInterval || 'month';
          const intervalCount = subscription.priceIntervalCount || 1;
          return (
            <div className="flex items-center gap-2">
              <Repeat className="text-muted-foreground h-4 w-4" />
              <span className="text-sm">{formatInterval(interval, intervalCount, t)}</span>
            </div>
          );
        },
        size: 120,
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: t('table.columns.amount'),
        cell: ({ row }) => {
          const amount = row.original.amount;
          const currency = row.original.currency || 'USD';

          if (!amount) {
            return <span className="text-muted-foreground">—</span>;
          }

          return (
            <div className="flex flex-col gap-0.5">
              <div className="text-sm font-semibold tabular-nums">{formatMoney(amount, currency)}</div>
              {currency !== 'USD' && (
                <div className="text-muted-foreground text-xs font-medium">{currency}</div>
              )}
            </div>
          );
        },
        size: 120,
      },
      {
        id: 'nextBillingDate',
        accessorKey: 'nextBillingDate',
        header: t('table.columns.nextBilling'),
        cell: ({ row }) => (
          <DateDisplay
            value={row.original.nextBillingDate}
            mode="date"
            className="text-sm tabular-nums"
          />
        ),
        size: 130,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const subscription = row.original;
          return <ActionsCell subscriptionId={subscription.id} />;
        },
        size: 60,
      },
    ],
    [t],
  );
}
